import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fetchCompany } from 'components/contacts/companies/api';
import { fetchPerson } from 'components/contacts/people/api';
import { clearErrors, modalRequestFinished, modalRequestSending } from '../project/actions';
import {
  BACKOFFICE_CONTACT,
  CONTACTS_COMPANY,
  CONTACTS_PERSON,
  contactSaveData,
  gatherDocAccessLevels,
  getStartingStatus,
  parseContactData,
  RADIO_OPTIONS,
  startingAccessLevel
} from './form/helper';
import LeadAlert from './form/lead_alert';
import ContactAlert from './form/contact_alert';
import FormDisplay from './form/form_display';
import { sharedLeadFormProps } from './lead_form_props';

export function LeadForm({
  availableDatabaseOptions = [],
  clearErrors,
  condoListings = [],
  crmContactsEnabled = false,
  errorMessage,
  hideLeadModal,
  lead,
  leadReferralSources = [],
  leadStatuses,
  leaseChecked,
  leaseSpaceListings = [],
  lotListings = [],
  modalRequestFinished,
  modalRequestSending,
  project,
  projectId,
  requestSending,
  saleChecked,
  saleListing,
  showAlert,
  onSave,
  useVaultGates
}) {
  const safeProject = { contacts: [], ...(project || {}) };
  const hasDatabases = availableDatabaseOptions.length > 0;
  // availableDatabaseOptions is empty for an apex account without crm/edge_crm - DatabasePolicy's
  // scope returns none for ordinary users - so the CRM typeahead has to be reachable without it.
  const canSearchContacts = hasDatabases || crmContactsEnabled;
  const docAccessLevels = gatherDocAccessLevels(useVaultGates);
  const findDocAccessLevel = access => docAccessLevels.find(level => level.value === access);
  const allLeadReferralSources = [{ id: '', label: 'No Referral Source' }, ...leadReferralSources];

  const contactIsPerson = lead?.contactType === CONTACTS_PERSON;
  const contactIsCompany = lead?.contactType === CONTACTS_COMPANY;

  const [radioSelected, setRadioSelected] = useState(canSearchContacts ?
    RADIO_OPTIONS.SEARCH_CONTACTS : RADIO_OPTIONS.NEW_PERSON);
  const [contactErrors, setContactErrors] = useState([]);
  const [person, setPerson] = useState({ phoneNumbers: [] });
  const [company, setCompany] = useState({});
  // Only a legacy Contacts::Person/Company contact gets fetched below. A lead sourced from the CRM
  // has neither, so there is nothing to wait on and the spinner would never clear.
  const [fetchingContact, setFetchingContact] = useState(contactIsPerson || contactIsCompany);
  const [leadData, setLeadData] = useState({
    listing: lead ? findListing(lead.listingId) : null,
    referralSource: lead ? findSource(lead.referralSource) : allLeadReferralSources[0],
    status: getStartingStatus(lead?.status, leadStatuses),
    saleAccessLevel:
      findDocAccessLevel(lead?.docAccessLevelForSale) || startingAccessLevel(useVaultGates),
    leaseAccessLevel:
      findDocAccessLevel(lead?.docAccessLevelForLease) || startingAccessLevel(useVaultGates),
    docAccessLevelForSale: lead?.docAccessLevelForSale || startingAccessLevel(useVaultGates).value,
    docAccessLevelForLease: lead?.docAccessLevelForLease || startingAccessLevel(useVaultGates).value,
    saleVaultGatesAccessLevelWithoutOverride:
      lead?.saleVaultGatesAccessLevelWithoutOverride || 'public_access',
    leaseVaultGatesAccessLevelWithoutOverride:
      lead?.leaseVaultGatesAccessLevelWithoutOverride || 'public_access',
    emailConsent: (lead?.marketingPreferences || []).includes('email'),
    smsConsent: (lead?.marketingPreferences || []).includes('sms'),
    phoneConsent: (lead?.marketingPreferences || []).includes('phone')
  });
  const [persistAddress, setPersistAddress] = useState(false);
  const [databaseContact, setDatabaseContact] = useState(null);
  const savedContactData = useRef();

  const referralSourceValid = lead || !!(leadData.referralSource?.id);

  function onRadioChange(newValue) {
    setRadioSelected(newValue);
    clearErrors();
  }

  useEffect(() => {
    if (!lead) return;

    if (contactIsPerson) {
      fetchPerson(lead.contactId)
        .then(setPerson)
        .finally(() => setFetchingContact(false));
    } else if (contactIsCompany) {
      fetchCompany(lead.contactId)
        .then((data) => {
          setCompany(data);
          setPersistAddress(!!data.address.id);
        })
        .finally(() => setFetchingContact(false));
    }
  }, []);

  function gatherAllListings() {
    const listings = [
      ...leaseSpaceListings,
      ...lotListings,
      ...condoListings
    ];

    if (saleListing) listings.unshift(saleListing);
    if (listings.length > 1) listings.unshift({ id: '', address2: '' });

    return listings;
  }

  const gatherLeadData = () => {
    const listings = gatherAllListings();
    let listingId = leadData.listing?.id;

    // In the case there is only one listing
    // auto set the listing to either what it was set to before, or the one existing listing
    if (listings.length === 1) {
      listingId = listingId || lead?.listingId || listings[0].id;
    }

    const data = {
      listingId,
      projectsLeadReferralSourceId: leadData.referralSource?.id,
      projectsLeadStatusId: leadData.status?.id,
      emailConsent: leadData.emailConsent,
      smsConsent: leadData.smsConsent,
      phoneConsent: leadData.phoneConsent
    };

    if (useVaultGates) {
      data.doc_access_level_for_lease = leadData.docAccessLevelForLease;
      data.doc_access_level_for_sale = leadData.docAccessLevelForSale;
    } else {
      data.doc_access_level_for_lease = leadData.leaseAccessLevel.value;
      data.doc_access_level_for_sale = leadData.saleAccessLevel.value;
    }

    if (contactIsPerson) {
      data.contactAttributes = person;
      data.contactAttributes.phoneNumbersAttributes = person.phoneNumbers;
    } else if (contactIsCompany) {
      data.contactAttributes = company;
      if (persistAddress) {
        data.contactAttributes.address_attributes = company.address;
      }
    }

    return data;
  };

  function saveContactAndLead() {
    const { url, data, methodType } = contactSaveData(radioSelected, person, projectId,
      savedContactData, company, persistAddress);
    methodType(url, data).then((contact) => {
      const contactData = parseContactData(contact);
      savedContactData.current = contactData;
      onSave(contactData, gatherLeadData());
    }).catch((e) => {
      if (typeof e === 'object' && e.length) {
        setContactErrors(e);
      } else {
        const errorMessage = `Lead could not be ${lead ? 'updated' : 'created'}.
          Please try again or contact support for help.`;
        setContactErrors([errorMessage]);
      }
      modalRequestFinished();
    });
  }

  // A contact picked out of the CRM has no legacy Contacts:: record to point at, so the lead is
  // identified by its backoffice contact id instead of a contact type/id pair.
  function selectedContactData(contact) {
    if (contact.associatedModelType === BACKOFFICE_CONTACT) {
      return { backofficeContactId: contact.associatedModelId, newContact: false };
    }

    return {
      associatedModelId: contact.associatedModelId,
      associatedModelType: contact.associatedModelType,
      newContact: false
    };
  }

  const handleSave = (e) => {
    e.preventDefault();
    if (!valid()) return;

    modalRequestSending();
    clearErrors();
    if (lead) {
      onSave(gatherLeadData());
    } else if (databaseContact && radioSelected === RADIO_OPTIONS.SEARCH_CONTACTS) {
      onSave(selectedContactData(databaseContact), gatherLeadData());
    } else {
      saveContactAndLead();
    }
  };

  function validContactData() {
    const fieldsToCheck = [];

    if (radioSelected === RADIO_OPTIONS.NEW_PERSON || contactIsPerson) {
      fieldsToCheck.push(...[person.firstName, person.lastName]);
    } else if (radioSelected === RADIO_OPTIONS.NEW_COMPANY || contactIsCompany) {
      fieldsToCheck.push(company.name);
    } else if (radioSelected === RADIO_OPTIONS.SEARCH_CONTACTS) {
      fieldsToCheck.push(databaseContact);
    }

    return fieldsToCheck.every(field => field !== '' && field);
  }

  const valid = () => !!(
    leadData.status?.id && referralSourceValid && (lead || validContactData())
  );

  function findListing(listingId) {
    return gatherAllListings().find(listing => listing.id === listingId);
  }

  function findSource(sourceId) {
    return allLeadReferralSources.find(referralSource => referralSource.id === sourceId);
  }

  return (
    <React.Fragment>
      <LeadAlert errorMessage={errorMessage} showAlert={showAlert}/>
      <ContactAlert contactErrors={contactErrors}/>
      {!lead && (
        <div className="d-flex align-items-center mb-4 gap-4">
          {canSearchContacts && (
            <label className="fw-normal d-flex align-items-center">
              <input
                checked={radioSelected === RADIO_OPTIONS.SEARCH_CONTACTS}
                className="mt-0 me-2"
                name="search-contacts-radio"
                type="radio"
                onChange={() => onRadioChange(RADIO_OPTIONS.SEARCH_CONTACTS)}
              />
              Search Contacts
            </label>
          )}

          <label className="fw-normal d-flex align-items-center">
            <input
              checked={radioSelected === RADIO_OPTIONS.NEW_PERSON}
              className="mt-0 me-2"
              name="new-person-radio"
              type="radio"
              onChange={() => onRadioChange(RADIO_OPTIONS.NEW_PERSON)}
            />
            New Person
          </label>

          <label className="fw-normal d-flex align-items-center">
            <input
              checked={radioSelected === RADIO_OPTIONS.NEW_COMPANY}
              className="mt-0 me-2"
              name="new-company-radio"
              type="radio"
              onChange={() => onRadioChange(RADIO_OPTIONS.NEW_COMPANY)}
            />
            New Company
          </label>
        </div>
      )}
      <FormDisplay
        allLeadReferralSources={allLeadReferralSources}
        company={company}
        contactIsPerson={contactIsPerson}
        crmContactsEnabled={crmContactsEnabled}
        databaseContact={databaseContact}
        docAccessLevels={docAccessLevels}
        fetchingContact={fetchingContact}
        handleSave={handleSave}
        hideLeadModal={hideLeadModal}
        lead={lead}
        leadData={leadData}
        leadStatuses={leadStatuses}
        leaseChecked={leaseChecked}
        listings={gatherAllListings()}
        persistAddress={persistAddress}
        person={person}
        project={safeProject}
        radioSelected={radioSelected}
        requestSending={requestSending}
        saleChecked={saleChecked}
        setCompany={setCompany}
        setDatabaseContact={setDatabaseContact}
        setLeadData={setLeadData}
        setPersistAddress={setPersistAddress}
        setPerson={setPerson}
        useVaultGates={useVaultGates}
        valid={valid()}
      />

    </React.Fragment>
  );
}



LeadForm.propTypes = {
  ...sharedLeadFormProps,
  availableDatabaseOptions: PropTypes.array.isRequired,
  clearErrors: PropTypes.func.isRequired,
  condoListings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    address2: PropTypes.string,
    number: PropTypes.string
  })),
  contacts: PropTypes.array,
  crmContactsEnabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  leadReferralSources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  leaseSpaceListings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    address2: PropTypes.string.isRequired
  })),
  lotListings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    address2: PropTypes.string,
    number: PropTypes.string
  })),
  modalRequestFinished: PropTypes.func.isRequired,
  modalRequestSending: PropTypes.func.isRequired,
  project: PropTypes.object,
  projectId: PropTypes.number.isRequired,
  saleListing: PropTypes.shape({
    id: PropTypes.number.isRequired
  }),
  showAlert: PropTypes.bool,
  onSave: PropTypes.func.isRequired
};

const mapStateToProps = ({ availableDatabaseOptions, project }) => ({
  availableDatabaseOptions: availableDatabaseOptions,
  project: project
});

const mapDispatchToProps = {
  clearErrors,
  modalRequestSending,
  modalRequestFinished
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadForm);
